import React, { useState, useRef, useEffect } from "react";

/**
 * LabelMentionInput Component
 * 
 * Props:
 * - value: string (controlled textarea value)
 * - onChange: (text: string) => void (callback for text change)
 * - onSubmit: () => void (callback when comment form is submitted)
 * - users: Array<{ uid: string, name: string }> (for @mention dropdown list)
 * - placeholder?: string
 * - disabled?: boolean
 */
export function LabelMentionInput({
  value,
  onChange,
  onSubmit,
  users = [],
  placeholder = "Write a comment...",
  disabled = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);

  // Helper to check if cursor is inside a mention word and extract the query
  const getMentionQuery = (text, cursorPos) => {
    const textBeforeCursor = text.slice(0, cursorPos);
    const lastAtIdx = textBeforeCursor.lastIndexOf("@");
    if (lastAtIdx === -1) return null;

    // Ensure there is a space before the '@', or it is the start of the string
    if (lastAtIdx > 0 && !/\s/.test(textBeforeCursor[lastAtIdx - 1])) {
      return null;
    }

    // Ensure there are no spaces between the '@' and the cursor
    const textAfterAt = textBeforeCursor.slice(lastAtIdx + 1);
    if (/\s/.test(textAfterAt)) {
      return null;
    }

    return textAfterAt;
  };

  const handleTextareaChange = (e) => {
    const text = e.target.value;
    onChange(text);

    const cursorPos = e.target.selectionStart;
    const mentionQuery = getMentionQuery(text, cursorPos);

    if (mentionQuery !== null) {
      setQuery(mentionQuery);
      setIsOpen(true);
      setSelectedIndex(0);
    } else {
      setIsOpen(false);
    }
  };

  const handleSelectUser = (user) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const text = value;
    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = text.slice(0, cursorPos);
    const lastAtIdx = textBeforeCursor.lastIndexOf("@");

    const beforeAt = text.slice(0, lastAtIdx);
    const afterCursor = text.slice(cursorPos);

    // Insert @username with a space
    const mentionText = `@${user.name} `;
    const newValue = beforeAt + mentionText + afterCursor;

    onChange(newValue);
    setIsOpen(false);
    setSelectedIndex(0);

    // Move cursor to directly after the inserted mention
    const newCursorPos = lastAtIdx + mentionText.length;
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Filter users based on query
  const filteredUsers = users.filter((u) =>
    u.name.toLowerCase().includes(query.toLowerCase())
  );

  const handleKeyDown = (e) => {
    if (isOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (filteredUsers.length > 0 ? (prev + 1) % filteredUsers.length : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (filteredUsers.length > 0 ? (prev - 1 + filteredUsers.length) % filteredUsers.length : 0));
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        if (filteredUsers[selectedIndex]) {
          handleSelectUser(filteredUsers[selectedIndex]);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
      }
    } else {
      // Support Ctrl+Enter or Cmd+Enter to submit
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (value.trim() && !disabled) {
          onSubmit();
        }
      }
    }
  };

  // Close dropdown if clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && event.target !== textareaRef.current) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Auto-scroll selected item into view in dropdown
  useEffect(() => {
    if (dropdownRef.current && isOpen) {
      const activeEl = dropdownRef.current.querySelector(".active-mention-item");
      if (activeEl) {
        activeEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex, isOpen]);

  const canSubmit = value.trim().length > 0 && !disabled;

  return (
    <div className="relative w-full flex flex-col border border-gray-300 rounded-lg focus-within:border-gray-400 focus-within:ring-1 focus-within:ring-gray-400 bg-white overflow-visible transition-all duration-150">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleTextareaChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full p-3 resize-none text-sm text-gray-800 placeholder-gray-400 focus:outline-none min-h-[90px] rounded-t-lg disabled:bg-gray-50 disabled:cursor-not-allowed"
      />

      {/* Mention Dropdown List */}
      {isOpen && !disabled && filteredUsers.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 left-2 bottom-full mb-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto"
        >
          <ul className="py-1">
            {filteredUsers.map((user, idx) => (
              <li
                key={user.uid}
                onClick={() => handleSelectUser(user)}
                className={`px-3 py-2 cursor-pointer text-sm transition-colors duration-150 select-none flex items-center gap-2 ${
                  idx === selectedIndex
                    ? "bg-indigo-50 text-indigo-900 active-mention-item"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-semibold text-indigo-700">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <span className="font-medium">{user.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer toolbar */}
      <div className="flex justify-between items-center px-3 py-2 bg-gray-50 border-t border-gray-100 rounded-b-lg">
        <span className="text-xs text-gray-400 select-none">
          Type @ to mention, Ctrl+Enter to comment
        </span>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          className={`px-4 py-1.5 rounded text-xs font-semibold shadow-sm transition-all duration-150 ${
            canSubmit
              ? "bg-red-500 hover:bg-red-600 text-white cursor-pointer"
              : "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200 shadow-none"
          }`}
        >
          Comment
        </button>
      </div>
    </div>
  );
}
